/* global data, leagueAverageData, serverParams */
import * as d3 from 'd3'
import debounce from 'lodash/debounce'

import { getPitchTypeColor } from 'bam-node-baseball-savant/utils/pitch-type-colors'
import { normalizeAccents } from 'bam-node-baseball-savant/utils/normalize-accents'
import { guff } from 'bam-node-baseball-savant/utils/guff'

const isTeam =
    serverParams.type === 'batting-team' ||
    serverParams.type === 'pitching-team'

const isPitcher = ['pitching-team', 'pitcher'].includes(serverParams.type)

const isLeague = serverParams.type === 'league'

const div = document.getElementById('viz-container')
const svg = d3.select('#bat-tracking-viz')

const narrowWidth = 500
let boundedWidth = svg.node().getBoundingClientRect().width
let isNarrow = boundedWidth <= narrowWidth
let boundedHeight = Number(svg.node().getAttribute('height')) || 500
// Make variable
const marginWide = { top: 80, right: 50, bottom: 50, left: 70 }
const marginNarrow = { top: 60, right: 20, bottom: 50, left: 50 }
let margin = isNarrow ? marginNarrow : marginWide
const imgSize = 60

let width = boundedWidth - margin.left - margin.right
let height = boundedHeight - margin.top - margin.bottom

setDims()

function setDims() {
    boundedWidth = svg.node().getBoundingClientRect().width
    isNarrow = window.innerWidth <= 767
    margin = isNarrow ? marginNarrow : marginWide

    svg.attr('height', isNarrow ? 700 : 600)

    boundedHeight = Number(svg.node().getAttribute('height')) || 500

    width = boundedWidth - margin.left - margin.right
    height = boundedHeight - margin.top - margin.bottom
}

const g = svg
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`)

const maxBubbles = 200

const title = g
    .append('text')
    .attr('class', 'title')
    .attr('transform', `translate(${width / 2} ,-20)`)
    .attr('font-size', '1.4rem')
    .attr('text-anchor', 'middle')
    .attr('fill', '#3a9098')
    .text('Statcast Bat Tracking Leaders')

const filteredHeight = width > 600 ? -20 : -6
const filteredText = g
    .append('text')
    .attr('class', 'filtered-text')
    .attr('transform', `translate(${width} ,${filteredHeight})`)
    .attr('font-size', '0.8rem')
    .attr('text-anchor', 'end')
    .attr('fill', '#3a9098')
    .text(`Showing Top ${maxBubbles} (by swings)`)
    .style('display', 'none')

const $xAxisEl = g
    .append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(0,${height})`)

const $yAxisEl = g.append('g').attr('class', 'y-axis')
const $xAxisLeagueAvg = g
    .append('path')
    .attr('class', 'league-average')
    .attr('stroke', 'red')
    .attr('stroke-dasharray', '2,2')
    .attr('stroke-opacity', 0.5)
    .attr('stroke-width', 2)

// y axis
const $yAxisLeagueAvg = g
    .append('path')
    .attr('class', 'league-average')
    .attr('stroke', 'red')
    .attr('stroke-dasharray', '2,2')
    .attr('stroke-opacity', 0.5)
    .attr('stroke-width', 2)

let dataset = data.slice()

const percentFormat = d3.format('.0%')
const tenthFormat = d3.format('.1f')

const uniqueIdentifier = 'id'

function uniqueId(row) {
    const secondaryIds = serverParams.secondaryGroupBy
        .map((prop) => row[prop])
        .join('-')
    const id = row[uniqueIdentifier] + secondaryIds
    return id
}

const rowName = 'name'

const categories = [
    { key: 'swings_qualified', label: 'Number of Swings' },
    { key: 'bat_contact_qualified', label: 'Number of Contact' },
    {
        key: 'avg_sweetspot_speed_mph_qualified',
        label: 'Avg. Bat Speed',
        format: (val) => guff(val, 'swing_speed_qualified'),
    },
    {
        key: 'avg_is_sweetspot_speed_high_qualified',
        label: 'Fast Swing %',
        format: (val) => guff(val, 'avg_is_sweetspot_speed_high_qualified'),
    },
    {
        key: 'attack_angle',
        label: 'Avg. Attack Angle',
        format: (val) => guff(val, 'bat_path_angle'),
    },
    {
        key: 'rate_ideal_attack_angle',
        label: 'Ideal Attack Angle %',
        format: percentFormat,
    },
    {
        key: 'attack_direction_pullopp',
        label: 'Avg. Attack Direction',
        format: (val) => guff(val, 'bat_path_angle'),
    },
    {
        key: 'avg_plane_vertical_angle',
        label: 'Avg. Swing Path - Tilt',
        format: (val) => guff(val, 'bat_path_angle'),
    },
    { key: 'squared_up_qualified', label: 'Number of Squared-Up Swings' },
    {
        key: 'squared_up_per_bat_contact',
        label: 'Squared-Up / Contact',
        format: (val) => guff(val, 'squared_up_per_bat_contact'),
    },
    {
        key: 'squared_up_per_swing',
        label: 'Squared-Up / Swing',
        format: (val) => guff(val, 'squared_up_per_swing'),
    },
    {
        key: 'squared_up_with_speed_qualified',
        label: 'Number of Blasts',
    },
    {
        key: 'squared_up_with_speed_per_bat_contact',
        label: 'Blasts / Contact',
        format: (val) => guff(val, 'squared_up_with_speed_per_bat_contact'),
    },
    {
        key: 'squared_up_with_speed_per_swing',
        label: 'Blasts / Swing',
        format: (val) => guff(val, 'squared_up_with_speed_per_swing'),
    },
    {
        key: 'swing_length_qualified',
        label: 'Swing Length',
        format: (val) => guff(val, 'swing_length_qualified'),
    },
    {
        key: 'hit_into_play_per_swing',
        label: 'Hit Into Play / Swing',
        format: percentFormat,
    },
    {
        key: 'avg_batter_x_position',
        label: 'Avg. Batting Stance X',
        format: (val) => guff(val, 'dist_in_inches'),
    },
    {
        key: 'avg_batter_y_position',
        label: 'Avg. Batting Stance Y',
        format: (val) => guff(val, 'dist_in_inches'),
    },
    {
        key: 'avg_intercept_x_vs_batter',
        label: 'Avg. Intercept X (vs batter)',
        format: (val) => guff(val, 'dist_in_inches'),
    },
    {
        key: 'avg_intercept_y_vs_batter',
        label: 'Avg. Intercept Y (vs batter)',
        format: (val) => guff(val, 'dist_in_inches'),
    },
    {
        key: 'swords',
        label: 'Swords',
    },
    {
        key: isPitcher ? 'pitcher_delta_run_exp' : 'delta_run_exp',
        label: isPitcher ? 'Pitcher Run Value' : 'Batter Run Value',
        format: tenthFormat,
    },
    {
        key: 'strikes_swinging_qualified',
        label: 'Number of Whiffs',
    },
    {
        key: 'strike_swinging_per_swing',
        label: 'Whiff Rate',
        format: percentFormat,
    },
    {
        key: 'hit_into_play_qualified',
        label: 'Balls Hit Into Play',
    },
    // {
    //     key: 'avg_plane_horizontal_angle_pullopp',
    //     label: 'Horizontal Swing Path',
    //     format: (val) => guff(val, 'bat_path_angle'),
    // },
    // {
    //     key: 'avg_sweetspot_max_accel',
    //     label: 'Max. Accel. (mph)',
    //     format: (val) => guff(val, 'bat_speed_avg'),
    // }
]

const groupByCategories = [
    // Group By Option
    {
        label: 'Pitch Type',
        key: 'api_pitch_type',
    },
    // End Group By Option
]

function getCategory(key) {
    return (
        categories.find((c) => c.key === key) ||
        groupByCategories.find((c) => c.key === key)
    )
}

function createScale({ key, domain }, xAxis) {
    const range = xAxis ? [0, width] : [height, 0]
    if (domain) {
        return d3.scaleLinear().domain(domain).range(range).nice()
    }
    let [min, max] = d3.extent(dataset, (d) => Number(d[key]))

    if (min === max) {
        const delta = Math.abs(0.2 * min)

        min -= delta
        max += delta
    }

    return d3.scaleLinear().domain([min, max]).range(range).nice()
}

function createRadiusScale({ key, domain, invertColor }) {
    const chartArea = width * height

    // Uh...roughly speaking
    const density = chartArea / dataset.length

    // 1200 -> 1
    // 3300 -> 2 or less
    // 6000 -> 2
    // 16000 -> 2.5

    // Rough guesstimating
    const logScale = d3
        .scaleLog()
        .domain([1200, 16000])
        .range([1, 2.4])
        .clamp(true)
    // const multiplier = boundedWidth <= 400 ? 1 : logScale(dataset.length)
    const multiplier = logScale(density)
    // const multiplier = 2
    const maxRadius = 8 * multiplier
    const minRadius = 4 * multiplier
    const defaultRadius = 6 * multiplier

    if (!key) {
        return () => defaultRadius
    }

    if (!domain) {
        const max = d3.max(data, (d) => Number(d[key]))
        // eslint-disable-next-line no-param-reassign
        domain = [0, max]
    }

    const range = invertColor ? [maxRadius, minRadius] : [minRadius, maxRadius]

    return d3.scaleSqrt().domain(domain).range(range).clamp(true)
}

function createColorScale({ key, colors, colorDomain, invertColor }) {
    // Special
    if (key === 'api_pitch_type') {
        return (row) => getPitchTypeColor(row)
    }

    if (!colors) {
        // colors = ['#3661AD', '#3661AD', '#ffffff', '#D82129', '#D82129']
        // eslint-disable-next-line no-param-reassign
        colors = ['#3661AD', '#ffffff', '#D82129']
    }

    if (!colorDomain) {
        const [min, max] = d3.extent(data, (d) => Number(d[key]))
        const avg = (min + max) / 2
        // eslint-disable-next-line no-param-reassign
        colorDomain = invertColor ? [max, avg, min] : [min, avg, max]
    }

    return d3.scaleLinear().domain(colorDomain).range(colors).clamp(true)
}

const $xLabel = g
    .append('text')
    .attr('id', 'xLabel')
    // .attr('transform', `translate(${width / 2} ,${height + margin.bottom / 2})`)
    .attr('transform', `translate(${width / 2} ,${height + 40})`)
    .style('text-anchor', 'middle')
    .style('font-size', '1rem')

const $yLabel = g
    .append('text')
    .attr('id', 'yLabel')
    .attr('transform', 'rotate(-90)')
    // .attr('y', 0 - margin.left / 2)
    .attr('y', 0 - margin.left / 2 - 10)
    .attr('x', 0 - height / 2)
    .attr('dy', '0em')
    .style('text-anchor', 'middle')
    .style('font-size', '1rem')

let nodes

function tick() {
    if (!nodes) return
    nodes.attr('transform', (d) => `translate(${d.x}, ${d.y})`)
}

const $tooltip = d3.select('#tooltip1')

const $xSelect = d3.select('#ddlXAxis').on('change', handleSelectChange)

$xSelect
    .selectAll('option')
    .data(categories)
    .join((enter) =>
        enter
            .append('option')
            .attr('value', (d) => d.key)
            .text((d) => d.label)
    )
const $ySelect = d3.select('#ddlYAxis').on('change', handleSelectChange)

$ySelect
    .selectAll('option')
    .data(categories)
    .join((enter) =>
        enter
            .append('option')
            .attr('value', (d) => d.key)
            .text((d) => d.label)
    )
const $colorSelect = d3.select('#ddlColor').on('change', handleSelectChange)

const colorGroupBy = serverParams.secondaryGroupBy
    .map((d) => getCategory(d))
    .filter((d) => d)

$colorSelect
    .selectAll('option')
    .data([
        { label: 'Images', key: 'headshots' },
        { label: 'None', key: '' },
        ...colorGroupBy,
        ...categories,
    ])
    .join((enter) =>
        enter
            .append('option')
            .attr('value', (d) => d.key)
            .text((d) => d.label)
    )

const $sizeSelect = d3.select('#ddlSize').on('change', handleSelectChange)

$sizeSelect
    .selectAll('option')
    .data([{ label: 'Uniform', key: '' }, ...categories])
    .join((enter) =>
        enter
            .append('option')
            .attr('value', (d) => d.key)
            .text((d) => d.label)
    )

function handleMouseEnter(_e, d) {
    const currentXKey = $xSelect.node().value
    const currentYKey = $ySelect.node().value

    const currentXInfo = getCategory(currentXKey)
    const currentYInfo = getCategory(currentYKey)

    const formattedValueX = currentXInfo.format
        ? currentXInfo.format(d[currentXKey])
        : d[currentXKey]
    const formattedValueY = currentYInfo.format
        ? currentYInfo.format(d[currentYKey])
        : d[currentYKey]

    $tooltip.style('display', 'block').html(
        `${d[rowName]}
        <div style="font-size: 14px">
        ${currentXInfo.label}: ${formattedValueX}
        <br/>
        ${currentYInfo.label}: ${formattedValueY}
        </div>
        `
    )

    nodes
        .filter((data) => data.id === d.id)
        .raise()
        .select('g')
        .transition()
        .ease(d3.easeElasticOut)
        .duration(300)
        .attr('transform', 'scale(0.75)')
}

function handleMouseLeave(_e, d) {
    const sizeKey = $sizeSelect.node().value
    const sizeInfo = getCategory(sizeKey) || {}
    const sizeScale = createRadiusScale(sizeInfo)

    $tooltip.style('display', 'none')

    nodes
        .filter((data) => data.id === d.id)
        .select('g')
        .transition()
        .duration(150)
        .attr(
            'transform',
            (data) => `scale(${(sizeScale(data[sizeKey]) * 2) / imgSize})`
        )
}

function handleMouseMove(e) {
    const rect = div.getBoundingClientRect()
    const x = e.clientX - rect.left // x position within the element.
    const y = e.clientY - rect.top // y position within the element.

    const buffer = 10
    if (x > (3 * boundedWidth) / 4) {
        $tooltip
            .style('left', 'auto')
            .style('right', `${boundedWidth - x - buffer}px`)
            .style('top', `${y + buffer}px`)
    } else {
        $tooltip
            .style('left', `${x + buffer}px`)
            .style('right', 'auto')
            .style('top', `${y + buffer}px`)
    }
}

function handleClick(d, sizeScale, sizeKey) {
    const selected = nodes.filter((data) => data.id === d.id)

    selected.raise()

    const labelClass = `label-${d.id}`
    const labels = svg.selectAll(`.${labelClass}`)

    if (!labels.empty()) {
        labels.remove()

        selected
            .select('.stroke-element')
            .attr('stroke-width', 3)
            .attr('stroke', '#666')

        selected.select('.fill-element').attr('fill', '#fff')
        return
    }

    selected
        .select('.stroke-element')
        .attr('stroke-width', 6)
        .attr('stroke', '#399098')

    selected.select('.fill-element').attr('fill', '#399098')

    const label = selected.append('g').attr('class', `label-${d.id}`)

    const labelHeight = 30
    const labelMarginX = 10
    const labelNudge = 5

    const radius = sizeScale(d[sizeKey])

    const bg = label
        .append('rect')
        .attr('fill', '#222')
        .attr('fill-opacity', 0.85)
        .attr('stroke', '#fff')
        .attr('stroke-width', 2)
        .attr('y', radius + labelNudge)
        .attr('height', labelHeight)
        .attr('rx', 5)

    const text = label
        .append('text')
        .attr('fill', '#399098')
        .attr('dominant-baseline', 'middle')
        .attr('text-anchor', 'middle')
        .attr('fill', 'white')
        .attr('y', radius + labelHeight / 2 + labelNudge)
        .attr('dy', 2)
        .text(d.name)

    const labelWidth = text.node().getComputedTextLength()

    bg.attr('width', labelWidth + 2 * labelMarginX).attr(
        'x',
        labelWidth / -2 - labelMarginX
    )
}

function handleSelectChange(shouldUpdateUrl = true) {
    const xKey = $xSelect.node().value
    const yKey = $ySelect.node().value
    const colorKey = $colorSelect.node().value
    const sizeKey = $sizeSelect.node().value

    updateDataset(xKey, yKey)
    updateChart(xKey, yKey, colorKey, sizeKey)
    if (shouldUpdateUrl) {
        updateUrl(xKey, yKey)
    }
}

function updateUrl(xKey, yKey) {
    const url = new URL(window.location)
    url.searchParams.set('vizX', xKey)
    url.searchParams.set('vizY', yKey)

    window.history.replaceState('', '', url.toString())
}

function updateDataset(xKey, yKey) {
    dataset = data
        .filter((d) => {
            const falsyValues = [undefined, null, NaN]
            return (
                !falsyValues.includes(d[xKey]) && !falsyValues.includes(d[yKey])
            )
        })
        .sort((a, b) =>
            Number(a.swings_qualified) > Number(b.swings_qualified) ? -1 : 1
        )
        .slice(0, maxBubbles)

    filteredText.style('display', data.length > maxBubbles ? 'block' : 'none')
}

const urlSearchParams = new URLSearchParams(window.location.search)

const initialXKey =
    urlSearchParams.get('vixX') || 'avg_sweetspot_speed_mph_qualified'
const initialYKey = urlSearchParams.get('vizY') || 'squared_up_per_swing'

$xSelect.property('value', initialXKey)
$ySelect.property('value', initialYKey)
$colorSelect.property('value', 'headshots')
$sizeSelect.property('value', 'swings_qualified')

updateDataset(initialXKey, initialYKey)

const simulation = d3.forceSimulation(dataset).on('tick', tick)

function createImageNode(enter, sizeScale, sizeKey, colorScale, colorKey) {
    const node = enter
        .append('g')
        .attr('class', (d) => `force node-${d.id}`)
        .attr('transform', (d) => `translate(${d.x}, ${d.y})`)
        .on('mouseenter', handleMouseEnter)
        .on('mouseleave', handleMouseLeave)
        .on('mousemove', handleMouseMove)
        .on('click', (_e, d) => handleClick(d, sizeScale, sizeKey))

    const basic = node
        .append('circle')
        .attr('class', 'circle-basic')
        .attr('r', (d) => sizeScale(d[sizeKey]))
        .attr('fill', (d) => colorScale(d[colorKey]))
        .attr('stroke', '#666')

    const scaledImg = node
        .append('g')
        .attr('class', 'circle-image')
        .attr('clip-path', 'url(#scatter-clip)')
        .attr(
            'transform',
            (d) => `scale(${(sizeScale(d[sizeKey]) * 2) / imgSize})`
        )

    handleImageTypes(colorKey, scaledImg, basic)

    scaledImg
        .append('circle')
        .attr('class', 'fill-element')
        .attr('fill', 'white')
        .attr('r', imgSize / 2)

        .attr('stroke', '#666')
        .attr('stroke-width', 2)

    scaledImg
        .append('image')
        .attr('href', (d) => {
            if (isLeague) {
                return '/site-core/images/savant-mlb.svg'
            }
            if (isTeam) {
                return `https://midfield.mlbstatic.com/v1/team/${d.id}/spots/120`
            }
            return `https://midfield.mlbstatic.com/v1/people/${d.id}/mlb/${imgSize}?circle=true`
        })
        .attr('x', -imgSize / 2)
        .attr('y', -imgSize / 2)
        .attr('height', imgSize)
        .attr('width', imgSize)

    scaledImg
        .append('circle')
        .attr('class', 'stroke-element')
        .attr('fill', 'none')
        .attr('r', imgSize / 2)
        .attr('stroke', '#666')
        .attr('stroke-width', 3)

    return node
}

function handleImageTypes(colorKey, imageNode, circleNode) {
    const showHeadshots = colorKey === 'headshots'

    imageNode.attr('opacity', showHeadshots ? 1 : 0)
    circleNode.attr('opacity', showHeadshots ? 0 : 1)
}

function updateChart(xKey, yKey, colorKey, sizeKey) {
    const xInfo = getCategory(xKey)
    const xScale = createScale(xInfo, true)

    const yInfo = getCategory(yKey)
    const yScale = createScale(yInfo, false)

    const xAxis = d3
        .axisBottom(xScale)
        .tickFormat(xInfo.format)
        .tickSize(-height)

    const yAxis = d3.axisLeft(yScale).tickFormat(yInfo.format).tickSize(-width)

    const colorInfo = getCategory(colorKey)
    const colorScale = colorInfo ? createColorScale(colorInfo) : () => '#fff'

    const sizeInfo = getCategory(sizeKey) || {}
    const sizeScale = createRadiusScale(sizeInfo)

    $xAxisEl
        .transition()
        .attr('transform', `translate(0,${height})`)
        .call(xAxis)
        .call((el) =>
            el
                .selectAll('.tick line')
                .attr('stroke-opacity', 0.3)
                .attr('stroke-dasharray', '5,5')
        )
        .call((el) => el.selectAll('.tick text').attr('dy', '10'))

    $yAxisEl
        .transition()
        .call(yAxis)
        .call((el) =>
            el
                .selectAll('.tick line')
                .attr('stroke-opacity', 0.3)
                .attr('stroke-dasharray', '5,5')
        )
        .call((el) => el.selectAll('.tick text').attr('dx', '-6'))

    $xLabel.text(xInfo.label)
    $yLabel.text(yInfo.label)

    // Add league averages
    // x axis
    $xAxisLeagueAvg.transition().attr(
        'd',
        d3.line()([
            [xScale(leagueAverageData[xKey]), 0],
            [xScale(leagueAverageData[xKey]), height],
        ])
    )

    // y axis
    $yAxisLeagueAvg.attr(
        'd',
        d3.line()([
            [0, yScale(leagueAverageData[yKey])],
            [width, yScale(leagueAverageData[yKey])],
        ])
    )

    if (!nodes) {
        // Needs for initial update
        dataset.forEach((d) => {
            // eslint-disable-next-line no-param-reassign
            d.x = xScale(d[xKey])
            // eslint-disable-next-line no-param-reassign
            d.y = yScale(d[yKey])
        })

        nodes = g
            .selectAll('g.force')
            .data(dataset, uniqueId)
            // .join((enter) => {
            //     const node = enter
            //         .append('g')
            //         .attr('class', 'force')
            //         .attr('transform', (d) => `translate(${d.x}, ${d.y})`)
            //         .on('mouseover', handleMouseOver)
            //         .on('mouseout', () => handleMouseOut(sizeScale, sizeKey))
            //         .on('mousemove', handleMouseMove)

            //     const scaledImg = node
            //         .append('g')
            //         .attr('clip-path', 'url(#scatter-clip)')
            //         .attr(
            //             'transform',
            //             (d) => `scale(${(sizeScale(d[sizeKey]) * 2) / imgSize})`
            //         )

            //     scaledImg
            //         .append('circle')
            //         .attr('fill', 'white')
            //         .attr('r', imgSize / 2)

            //         .attr('stroke', '#666')
            //         .attr('stroke-width', 2)

            //     scaledImg
            //         .append('image')
            //         .attr(
            //             'href',
            //             (d) =>
            //                 `https://img.mlbstatic.com/mlb-photos/image/upload/w_${imgSize},d_people:generic:headshot:silo:current.png,q_auto:best,f_auto/v1/people/${d.id}/headshot/silo/current`
            //         )
            //         .attr('x', -imgSize / 2)
            //         .attr('y', -imgSize / 2)
            //         .attr('height', imgSize)
            //         .attr('width', imgSize)

            //     scaledImg
            //         .append('circle')
            //         .attr('fill', 'none')
            //         .attr('r', imgSize / 2)
            //         .attr('stroke', '#666')
            //         .attr('stroke-width', 2)

            //     // node.append('circle')
            //     //     .attr('r', (d) => sizeScale(d[sizeKey]))
            //     //     .attr('fill', (d) => colorScale(d[colorKey]))
            //     //     .attr('stroke', '#666')

            //     return node
            // })
            .join((enter) =>
                createImageNode(enter, sizeScale, sizeKey, colorScale, colorKey)
            )

        tick()
    } else {
        dataset.forEach((d) => {
            // eslint-disable-next-line no-param-reassign
            d.x = d.x || xScale(d[xKey])
            // eslint-disable-next-line no-param-reassign
            d.y = d.y || yScale(d[yKey])
        })

        nodes = nodes.data(dataset, uniqueId).join(
            (enter) =>
                createImageNode(
                    enter,
                    sizeScale,
                    sizeKey,
                    colorScale,
                    colorKey
                ),
            (update) => {
                const imageNode = update.select('g.circle-image')
                const basicNode = update.select('circle.circle-basic')

                handleImageTypes(colorKey, imageNode, basicNode)

                imageNode
                    .transition()
                    .attr(
                        'transform',
                        (d) => `scale(${(sizeScale(d[sizeKey]) * 2) / imgSize})`
                    )

                basicNode
                    .transition()
                    .attr('r', (d) => sizeScale(d[sizeKey]))
                    .attr('fill', (d) => colorScale(d[colorKey]))
                return update
            },
            (exit) => exit.transition().attr('opacity', 0).remove()
        )

        simulation.nodes(dataset)
    }

    simulation
        .nodes(dataset)
        .force('x', d3.forceX((d) => xScale(d[xKey])).strength(0.5))
        .force('y', d3.forceY((d) => yScale(d[yKey])).strength(0.5))
        .force(
            'collide',
            d3.forceCollide((d) => sizeScale(d[sizeKey]) * 0.8).strength(0.5)
        )

    // Run the layout on the simulation's own ticker rather than blocking the
    // main thread with a synchronous `simulation.tick(50)`. forceCollide is
    // expensive (~8-15ms per tick for this many overlapping bubbles), so doing
    // 50 of them at once froze the page for ~400ms. Letting the ticker spread
    // that work across animation frames keeps the UI responsive and animates
    // the bubbles into place. alphaDecay controls how quickly it settles.
    simulation.alpha(0.6).alphaDecay(0.1).velocityDecay(0.5).restart()
}

// Start automatically
handleSelectChange(false)

export default function updatePlayerSearch(name) {
    const normalName = normalizeAccents(name)

    if (normalName.trim() === '') {
        nodes.attr('opacity', 1)
        return
    }

    const ids = new Set(
        dataset
            .filter((d) => normalizeAccents(d.name).includes(normalName))
            .map((d) => d.id)
    )

    nodes.attr('opacity', (d) => (ids.has(d.id) ? 1 : 0.2))
}

svg.append('defs')
    .append('clipPath')
    .attr('id', 'scatter-clip')
    .append('circle')
    .attr('cx', 0)
    .attr('cy', 0)
    .attr('r', imgSize / 2 + 2)

window.addEventListener(
    'resize',
    debounce(() => {
        setDims()

        g.attr('transform', `translate(${margin.left},${margin.top})`)

        const xKey = $xSelect.node().value
        const yKey = $ySelect.node().value
        const colorKey = $colorSelect.node().value
        const sizeKey = $sizeSelect.node().value
        updateChart(xKey, yKey, colorKey, sizeKey)

        // Fix other thangs
        title.attr('transform', `translate(${width / 2} ,-20)`)
        $xLabel.attr('transform', `translate(${width / 2} ,${height + 45})`)
        $yLabel.attr('y', 0 - margin.left / 2 - 10).attr('x', 0 - height / 2)

        const newFilteredHeight = width > 600 ? -20 : -8
        filteredText.attr(
            'transform',
            `translate(${width} , ${newFilteredHeight})`
        )
    }, 300)
)
